import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { DEFAULT_LOCALE, LOCALES, localeFromPath, pageLocale, preferredLocale, rememberLocale } from './locales';
import { isLocaleLoaded, loadLocale, makeT } from './translate';

const LocaleContext = createContext({
  locale: DEFAULT_LOCALE,
  preferred: DEFAULT_LOCALE,
  setPreferred: () => {},
  t: makeT(DEFAULT_LOCALE),
});

/**
 * Which language the page is in. A /es or /pt URL says so and is remembered
 * as the visitor's choice; the signed-in app follows that choice (or the
 * browser); English marketing pages stay English.
 */
export function LocaleProvider({ children }) {
  const { pathname } = useLocation();
  const [preferred, setPreferredState] = useState(() => (typeof window === 'undefined' ? DEFAULT_LOCALE : preferredLocale()));
  const fromPath = localeFromPath(pathname);

  useEffect(() => {
    if (fromPath && fromPath !== preferred) {
      rememberLocale(fromPath);
      setPreferredState(fromPath);
    }
  }, [fromPath]); // eslint-disable-line react-hooks/exhaustive-deps

  const setPreferred = useCallback((locale) => {
    if (!LOCALES.includes(locale)) return;
    rememberLocale(locale);
    setPreferredState(locale);
  }, []);

  const locale = pageLocale(pathname, preferred);
  // Strings come in the language whose dictionary is here: the page's own is loaded
  // before the first render (index.jsx), and on a switch to another the last one stays
  // up for the moment it takes to arrive, rather than English or raw keys
  const [spoken, setSpoken] = useState(() => (isLocaleLoaded(locale) ? locale : DEFAULT_LOCALE));
  useEffect(() => {
    let current = true;
    loadLocale(locale).then(() => { if (current) setSpoken(locale); }).catch(() => {});
    return () => { current = false; };
  }, [locale]);
  const shown = isLocaleLoaded(locale) ? locale : spoken;
  const value = useMemo(() => ({ locale, preferred, setPreferred, t: makeT(shown) }), [locale, preferred, setPreferred, shown]);
  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export const useLocale = () => useContext(LocaleContext);
export const useT = () => useContext(LocaleContext).t;
